import { useTranslation } from 'react-i18next';

import { Ltr } from '@/components/amount';
import { Button } from '@/components/ui/button';

/**
 * THE PAGER, once — NFR-42's visible half.
 *
 * PART 4's list conventions, dispositioned here at the shared control:
 * GEN-29's counter and page steps are this component (the block size is the
 * settled 50, not the rule's 25 — recorded); GEN-27 (row click opens the
 * record) and GEN-26 (a Gated New at the trailing end) hold on every list
 * page; GEN-31/GEN-32's empty states are each list's own worded message.
 * REPORTED, not built: GEN-28 column-heading sort (no table anywhere sorts
 * by click — the known-unknowns row, unchanged) and GEN-25's free-text box
 * on the handful of lists that carry only status filters. GEN-24's 0.3s
 * page fade is deliberately NOT added by this wave: a global mount
 * animation risks "element is not stable" flake across the 29-journey
 * suite — owner/orchestrator call.
 *
 * DSH-09 §5f is the reference: "the range, the total, and the two steps", with
 * both ends disabled at their end. Every other list gets the same control from
 * this file rather than forty near-identical copies, because the parts that
 * are easy to get subtly wrong — the range arithmetic when the total is zero,
 * `aria-live` on the page indicator, LTR-pinning the numerals in an RTL
 * layout (N-9/N-10) — are exactly the parts that get copied wrong.
 *
 * It takes the meta a paged response already carries and nothing else, so a
 * screen cannot draw a range that disagrees with the rows above it.
 */
export interface PagerProps {
  page: number;
  pages: number;
  first: number;
  last: number;
  total: number;
  onPage: (page: number) => void;
  /** Names the list for a screen reader: "Items pages". */
  label: string;
  /**
   * Optional: a caller with per-row expansion state (the audit log's open
   * row) closes it when the page moves.
   */
  onLeave?: (() => void) | undefined;
}

export function Pager({
  page,
  pages,
  first,
  last,
  total,
  onPage,
  label,
  onLeave,
}: PagerProps) {
  const { t } = useTranslation();

  function go(next: number) {
    onLeave?.();
    onPage(next);
  }

  return (
    <div
      className="flex flex-wrap items-center gap-3 text-sm text-muted-foreground"
      role="navigation"
      aria-label={label}
    >
      <span className="tabular-nums">{t('paging.showing', { first, last, total })}</span>
      <div className="ms-auto flex items-center gap-2">
        <Button variant="outline" size="sm" disabled={page <= 1} onClick={() => go(page - 1)}>
          {t('paging.previous')}
        </Button>
        {/*
          `aria-live` so a screen-reader user hears the page change: the rows
          below it are not announced, and without this the two buttons are the
          only feedback that anything happened.

          NFR-41 — "a change made without reloading the page must be announced
          to a screen reader" — this live region is the rule's shared
          enforcement for every paged list; the product-wide convention for
          the rule's other two cases is `role="alert"` on every inline error
          and `role="status"` on save confirmations, applied at each site.
        */}
        <span className="tabular-nums" aria-live="polite">
          <Ltr value={`${page} / ${pages}`} />
        </span>
        <Button
          variant="outline"
          size="sm"
          disabled={page >= pages}
          onClick={() => go(page + 1)}
        >
          {t('paging.next')}
        </Button>
      </div>
    </div>
  );
}
